import { ConfigOption } from '@ngx-formly/core';
import { InputSsnMaskComponent } from './input-ssn-mask.component';
import { FormlyWrapperAddonsComponent } from './addons.wrapper';
import { addonsExtension } from './addons.extension';
import { FormlyFieldTextComponent } from './text.component';
import { FormlyDatepickerTypeComponent } from './date.component';
import { FormlyAutocompleteTypeComponent } from './autocomplete-type.component';
import { MaxMinValidator, UrlValidator } from './formly-validators';
import { FormlyCurrencyTypeComponent } from './formly-currency-type.component';
import { FormlyTextareaAutoResizeComponent } from './formly-textarea-autoresize-type.component';
import { StateSelectComponent } from './state-select.component';
import { CountrySelectComponent } from './country-select.components';
import { LabelComponent } from './label.component';
import { ZoneSelectComponent } from './zone-select.component';
import { LabeledMultiSelectComponent } from './labeled-multi-select.component';
import { PanelWrapperComponent } from './panel-wrapper.component';
import { FormlyRepeatInputComponent } from 'ng2-app/form-core/formly/repeat-input.component';
import { BasicLabeledMultiSelectComponent } from './basic-labeled-multi-select.component';
import { IndeterminateCheckboxComponent } from 'ng2-app/form-core/formly/indeterminate-checkbox/indeterminate-checkbox.component';
import { FormFieldGroupComponent } from './form-field-group-type';
import { PhoneNumberInputComponent } from './phone-number-input.component';
import { CountryCodeSelectComponent } from './country-code-select.component';
import { FormlyValidationWrapperComponent } from './validation-wrapper.component';
import { RepeatDeleteComponent } from './repeat-delete.component';
import { RepeatSectionTypeComponent } from './repeat-section-type.component';

export interface FormlyModel {
  [key: string]: string | string[] | null;
}

export const FIELD_TYPE_COMPONENTS = [
  InputSsnMaskComponent,
  FormlyWrapperAddonsComponent,
  FormlyValidationWrapperComponent,
  FormlyFieldTextComponent,
  FormlyDatepickerTypeComponent,
  FormlyAutocompleteTypeComponent,
  FormlyCurrencyTypeComponent,
  IndeterminateCheckboxComponent,
  FormlyRepeatInputComponent,
  FormlyTextareaAutoResizeComponent,
  FormFieldGroupComponent,
  LabelComponent,
  StateSelectComponent,
  PhoneNumberInputComponent,
  CountryCodeSelectComponent,
  CountrySelectComponent,
  ZoneSelectComponent,
  LabeledMultiSelectComponent,
  BasicLabeledMultiSelectComponent,
  RepeatDeleteComponent,
  RepeatSectionTypeComponent
];

export const FORMLY_CONFIG: ConfigOption = {
  types: [
    {
      name: 'text',
      component: FormlyFieldTextComponent,
      wrappers: ['form-field'],
    },
    {
      name: 'text',
      component: FormlyFieldTextComponent,
      wrappers: ['form-field'],
    },
    {
      name: 'number',
      extends: 'text',
      defaultOptions: {
        templateOptions: {
          ngxMask: {
            mask: 'separator.2',
            thousandSeparator: ',',
          }
        }
      }
    },
    {
      name: 'unseparated-number',
      extends: 'text',
      defaultOptions: {
        templateOptions: {
          ngxMask: {
            mask: '0*'
          }
        }
      }
    },
    {
      name: 'date',
      component: FormlyDatepickerTypeComponent,
      wrappers: ['form-field'],
      defaultOptions: {
        defaultValue: new Date(),
        templateOptions: {
          datepickerOptions: {},
        },
      },
    },
    {
      name: 'percent',
      extends: 'number',
      defaultOptions: {
        templateOptions: {
          ngxMask: {
            suffix: '%'
          }
        }
      }
    },
    {
      name: 'currency',
      component: FormlyCurrencyTypeComponent,
      extends: 'number',
      defaultOptions: {
        templateOptions: {
          floatLabel: 'always',
          ngxMask: {
            mask: 'separator.2',
            thousandSeparator: ',',
          }
        }
      }
    },
    {
      name: 'bool',
      extends: 'radio',
      defaultOptions: {
        templateOptions: {
          appearance: 'legacy',
          color: 'primary',
          options: [
            { value: 1, label: 'Yes' },
            { value: 0, label: 'No' },
          ]
        },
      },
    },
    {
      name: 'phone',
      extends: 'text',
      defaultOptions: {
        templateOptions: {
          ngxMask: {
            mask: '(000) 000-0000'
          }
        }
      }
    },
    {
      name: 'autocomplete',
      component: FormlyAutocompleteTypeComponent,
      wrappers: ['form-field'],
    },
    {
      name: 'intl-phone',
      component: FormFieldGroupComponent,
      defaultOptions: {
        fieldGroup: [
          { type: 'country-code-select', key: 'countryCode', className: 'formly-sub-column' },
          { type: 'phone-phone', key: 'phone', wrappers: [], className: 'formly-sub-column-3' },
        ]
      }
    },
    {
      name: 'states',
      component: StateSelectComponent,
      wrappers: ['form-field']
    },
    {
      name: 'phone-phone',
      component: PhoneNumberInputComponent,
      extends: 'number'
    },
    {
      name: 'country-code-select',
      component: CountryCodeSelectComponent,
    },
    {
      name: 'countries',
      component: CountrySelectComponent,
      wrappers: ['form-field']
    },
    {
      name: 'zones',
      component: ZoneSelectComponent,
      wrappers: ['form-field']
    },
    { // TODO cleanup
      name: 'input-ssn-mask',
      component: InputSsnMaskComponent,
      wrappers: ['form-field']
    },
    {
      name: 'labeled-multi-select',
      component: LabeledMultiSelectComponent,
    },
    {
      name: 'padded-label',
      component: LabelComponent,
      defaultOptions: {
        templateOptions: {
          appearance: 'legacy',
          label: 'Test Label'
        },
      },
    },
    {
      name: 'basic-labeled-multi-select',
      component: BasicLabeledMultiSelectComponent,
    },
    {
      name: 'field-group',
      component: FormFieldGroupComponent,
   },
    {
      name: 'indeterminate-checkbox',
      component: IndeterminateCheckboxComponent,
    },
    {
      name: 'ssn',
      extends: 'text',
      defaultOptions: {
        templateOptions: {
          ngxMask: {
            mask: 'BBB-BB-0000'
          }
        }
      }
    },
    {
      name: 'textarea-auto-resize',
      component: FormlyTextareaAutoResizeComponent,
      wrappers: ['form-field']
    },
    {
      name: 'repeat-input',
      component: FormlyRepeatInputComponent,
    },
    {
      name: 'repeat-section',
      component: RepeatSectionTypeComponent
    },
    {
      name: 'repeat-delete',
      component: RepeatDeleteComponent
    }
  ],
  validators: [
    { name: 'url', validation: UrlValidator },
    { name: 'maxMin', validation: MaxMinValidator },
  ],
  validationMessages: [
    { name: 'required', message: 'This field is required' },
    { name: 'pattern', message: 'Not in correct format' },
    { name: 'value', message: 'Not correct value' },
    { name: 'date', message: 'Invalid date' },
    { name: 'min', message: 'Number is too low' },
    { name: 'max', message: 'Number is too high' },
    { name: 'minLength', message: 'Text is too short' },
    { name: 'maxLength', message: 'Text is too long' },
    { name: 'url', message: 'Enter a valid url (including http:// or https:// - example: https://www.lendio.com)' },
  ],
  wrappers: [
    { name: 'addons', component: FormlyWrapperAddonsComponent },
    { name: 'panel', component: PanelWrapperComponent },
    { name: 'validationWrapper', component: FormlyValidationWrapperComponent },
  ],
  extensions: [
    { name: 'addons', extension: { onPopulate: addonsExtension } },
  ],
};
